import keyBy from "lodash-es/keyBy";
import { observable, makeObservable, action } from "mobx";
import { inject, observer } from "mobx-react";
import { Component } from "react";
import { FormattedMessage } from "react-intl";

import { Link } from "react-router-dom";
import { compose } from "nvent-web/utils/compose";

import PageTitle from "nvent-web/App/components/PageTitle";
import LinkButton from "nvent-web/components/Button/LinkButton";
import { LoadingSection } from "nvent-web/components/LoadingSection/LoadingSection";
import ProgressFull from "nvent-web/components/Progress/FullProgress";
import { ProductsStore } from "nvent-web/stores/Products";
import { ProjectsStore } from "nvent-web/stores/Projects";
import { RoomsStore } from "nvent-web/stores/Rooms";
import { type TestType } from "nvent-web/types/Test";

import Test, { type TestFormValues } from "./components/Test";
import TestProgress from "./components/TestProgress";
import style from "./Tests.module.scss";
import { withRouter, type WithRouterProps } from "~/nvent-web/utils/withRouter";

interface ProductTestParams {
  projectId: string;
  roomId: string;
  productId: string;
}

interface TestsProps extends ProductTestParams, WithRouterProps {
  products: ProductsStore;
  projects: ProjectsStore;
  rooms: RoomsStore;
}

const stepsOrder: TestType[] = [
  "pre_installation",
  "post_installation",
  "after_floor_covering",
];

const TEST_COUNT = stepsOrder.length;

export class Tests extends Component<TestsProps> {
  isLoading = false;

  step?: TestType;

  constructor(props: TestsProps) {
    super(props);

    makeObservable(this, {
      step: observable,
      setStepValue: action.bound,
    });
  }

  private get params() {
    const rawParams = this.props.params;

    return {
      ...rawParams,
      projectId: Number(rawParams.projectId),
      roomId: Number(rawParams.roomId),
      productId: Number(rawParams.productId),
    };
  }

  async componentDidMount() {
    await this.setStep();
  }

  setStepValue(value: typeof this.step) {
    this.step = value;
  }

  async setStep() {
    const { products, projects, rooms } = this.props;
    const { projectId, roomId, productId } = this.params;

    this.isLoading = true;

    await projects.getProject(projectId);
    await rooms.getRoom(projectId, roomId);

    const product = await products.getProduct(projectId, roomId, productId);

    const testsByStep = keyBy(product.tests, "type");

    const step = stepsOrder.find((stepType) => {
      const test = testsByStep[stepType];
      return !test;
    });

    this.setStepValue(step);

    this.isLoading = false;
  }

  render() {
    const { singleProduct, isProductLoading } = this.props.products;
    const { projectDetails } = this.props.projects;
    const { roomDetails } = this.props.rooms;

    if (this.isLoading || isProductLoading) {
      return <LoadingSection />;
    }

    let allTestsAreComplete;

    if (singleProduct) {
      allTestsAreComplete =
        singleProduct.tests.filter((test) => test.completed).length ===
        TEST_COUNT;
    }

    return (
      <>
        <PageTitle>
          <FormattedMessage id="tests.title" />
        </PageTitle>
        {projectDetails && roomDetails && allTestsAreComplete && (
          <Link to={`/projects/${projectDetails.id}/rooms/${roomDetails.id}`}>
            {roomDetails.name}
          </Link>
        )}
        {singleProduct && (
          <>
            <h2 className={style.productName}>{singleProduct.description}</h2>
            <ProgressFull
              progress={
                ((100 / 3) *
                  singleProduct.tests.filter((test) => test.completed).length) /
                100
              }
              hideLabel
            />
            <TestProgress
              step={this.step}
              product={singleProduct}
              onTestItemClick={this.switchStep}
            />
            {this.step && (
              <Test
                product={singleProduct}
                step={this.step}
                onSubmit={this.onSubmit}
              />
            )}
            <div className={style.actions}>
              {projectDetails && roomDetails && allTestsAreComplete && (
                <LinkButton
                  className={style.button}
                  theme="primaryBlue"
                  to={`/projects/${projectDetails.id}/rooms/${roomDetails.id}`}
                >
                  <FormattedMessage id="actions.finish" />
                </LinkButton>
              )}
            </div>
          </>
        )}
      </>
    );
  }

  private switchStep = (step: TestType) => {
    this.setStepValue(step);
  };

  private onSubmit = async (values: TestFormValues, step: string) => {
    const { products } = this.props;
    const { projectId, roomId, productId } = this.params;

    await products.testProduct(
      projectId,
      roomId,
      productId,
      values.heatResistance,
      values.insulationResistance,
      step
    );

    if (
      values.batchNumber !== undefined ||
      values.reflectaSerialNumber !== undefined
    ) {
      await products.setProductBatchNumber({
        projectId,
        roomId,
        productId,
        batchNumber: values.batchNumber || null,
        reflectaSerialNumber: values.reflectaSerialNumber || null,
      });
    }

    await this.setStep();
  };
}

const TestsComponent = compose(
  inject("products", "projects", "rooms"),
  withRouter,
  observer
)(Tests);

TestsComponent.displayName = "Tests";

export default TestsComponent;
